import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getCustomerRequestById,
  replyToCustomerRequest,
  updateCustomerRequestStatus,
  STATUS_ENUM,
} from '../../services/CustomerRequestsService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Footer from '../../Footer';

const STATUS_COLORS = {
  Pending: { bg: '#ffc107', text: '#000' },
  Notified: { bg: '#0d6efd', text: '#fff' },
  CustomerResponded: { bg: '#198754', text: '#fff' },
  Resolved: { bg: '#6c757d', text: '#fff' },
  Escalated: { bg: '#dc3545', text: '#fff' },
};

function CustomerRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolvedBy, setResolvedBy] = useState('');

  const fetchRequest = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCustomerRequestById(id);
      setRequest(data);
    } catch (err) {
      setError('Failed to load customer request');
      toast.error('Failed to load customer request');
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    fetchRequest();
  }, [fetchRequest]);

  const handleSendReply = async () => {
    if (!replyMessage.trim()) {
      toast.warning('Please enter a message');
      return;
    }

    setSendingReply(true);
    try {
      await replyToCustomerRequest(id, replyMessage);
      toast.success('Reply sent successfully!');
      setReplyMessage('');
      fetchRequest(); // Refresh the data
    } catch (err) {
      toast.error('Failed to send reply');
    }
    setSendingReply(false);
  };

  const handleStatusUpdate = async (newStatus) => {
    if (newStatus === STATUS_ENUM.Resolved) {
      setShowResolveModal(true);
      return;
    }

    setUpdatingStatus(true);
    try {
      await updateCustomerRequestStatus(id, newStatus);
      toast.success('Status updated successfully!');
      fetchRequest(); // Refresh the data
    } catch (err) {
      toast.error('Failed to update status');
    }
    setUpdatingStatus(false);
  };

  const handleResolve = async () => {
    if (!resolvedBy.trim()) {
      toast.warning('Please enter your name');
      return;
    }

    setUpdatingStatus(true);
    try {
      await updateCustomerRequestStatus(id, STATUS_ENUM.Resolved, resolvedBy);
      toast.success('Request resolved successfully!');
      setShowResolveModal(false);
      setResolvedBy('');
      fetchRequest(); // Refresh the data
    } catch (err) {
      toast.error('Failed to resolve request');
    }
    setUpdatingStatus(false);
  };

  const getStatusBadge = (status) => {
    const colors = STATUS_COLORS[status] || { bg: '#6c757d', text: '#fff' };
    return (
      <span
        className="badge"
        style={{
          backgroundColor: colors.bg,
          color: colors.text,
          padding: '0.5em 1em',
          borderRadius: '0.25rem',
          fontSize: '0.9rem',
        }}
      >
        {status}
      </span>
    );
  };

  const formatJson = (data) => {
    try {
      if (typeof data === 'string') {
        const parsed = JSON.parse(data);
        return JSON.stringify(parsed, null, 2);
      }
      return JSON.stringify(data, null, 2);
    } catch {
      return data;
    }
  };

  if (loading) {
    return (
      <div style={{ paddingLeft: 25, paddingRight: 25, width: '100%', minHeight: '100vh' }} className="mt-4">
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !request) {
    return (
      <div style={{ paddingLeft: 25, paddingRight: 25, width: '100%', minHeight: '100vh' }} className="mt-4">
        <div className="alert alert-danger">{error || 'Request not found'}</div>
        <button className="btn btn-secondary" onClick={() => navigate('/customer-requests')}>
          <i className="bi bi-arrow-left me-2"></i>
          Back to List
        </button>
      </div>
    );
  }

  return (
    <div style={{ paddingLeft: 25, paddingRight: 25, width: '100%', minHeight: '100vh' }} className="mt-4">
      <ToastContainer />

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-3">
          <button className="btn btn-outline-secondary" onClick={() => navigate('/customer-requests')}>
            <i className="bi bi-arrow-left"></i>
          </button>
          <h4 className="mb-0">Event #{request.id}</h4>
          {request.customerRequest?.status && getStatusBadge(request.customerRequest.status)}
        </div>
        <button
          className="btn btn-outline-secondary d-flex align-items-center"
          onClick={fetchRequest}
          disabled={loading}
        >
          <i className="bi bi-arrow-clockwise me-2"></i>
          Refresh
        </button>
      </div>

      <div className="row">
        {/* Left Column */}
        <div className="col-lg-8">
          {/* Section 1: Slack Event Info */}
          <div className="card mb-4">
            <div className="card-header bg-light">
              <h5 className="mb-0">
                <i className="bi bi-slack me-2"></i>
                Slack Event Information
              </h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="text-muted small">Event Type</label>
                  <div>{request.eventType || '-'}</div>
                </div>
                <div className="col-md-6">
                  <label className="text-muted small">Slack Channel</label>
                  <div>
                    {request.channelName || '-'}
                    {request.channelId && (
                      <span className="text-muted font-monospace small ms-2">({request.channelId})</span>
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <label className="text-muted small">Slack User</label>
                  <div>{request.slackUsername || request.slackUserId || '-'}</div>
                </div>
                <div className="col-md-6">
                  <label className="text-muted small">Event Timestamp</label>
                  <div className="font-monospace small">{request.eventTs || '-'}</div>
                </div>
                <div className="col-md-6">
                  <label className="text-muted small">Processed</label>
                  <div>
                    {request.isProcessed ? (
                      <span className="badge bg-success">Yes</span>
                    ) : (
                      <span className="badge bg-warning text-dark">No</span>
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <label className="text-muted small">Event Created</label>
                  <div>{new Date(request.createdAt).toLocaleString()}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Customer Request Info */}
          {request.customerRequest ? (
            <div className="card mb-4">
              <div className="card-header bg-light">
                <h5 className="mb-0">
                  <i className="bi bi-info-circle me-2"></i>
                  Request Information
                </h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="text-muted small">Tracking Number</label>
                    <div className="fw-bold text-primary fs-5">{request.customerRequest.trackingNumber}</div>
                  </div>
                  <div className="col-md-6">
                    <label className="text-muted small">Request Type</label>
                    <div>{request.customerRequest.requestType || '-'}</div>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Notes</label>
                    <div>{request.customerRequest.notes || '-'}</div>
                  </div>
                  <div className="col-md-6">
                    <label className="text-muted small">Portal URL</label>
                    <div>
                      {request.customerRequest.portalUrl ? (
                        <a href={request.customerRequest.portalUrl} target="_blank" rel="noopener noreferrer">
                          {request.customerRequest.portalUrl}
                          <i className="bi bi-box-arrow-up-right ms-1"></i>
                        </a>
                      ) : (
                        '-'
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="text-muted small">Status</label>
                    <div>{getStatusBadge(request.customerRequest.status)}</div>
                  </div>
                  <div className="col-md-6">
                    <label className="text-muted small">Created</label>
                    <div>{new Date(request.customerRequest.createdAt).toLocaleString()}</div>
                  </div>
                  <div className="col-md-6">
                    <label className="text-muted small">Updated</label>
                    <div>{request.customerRequest.updatedAt ? new Date(request.customerRequest.updatedAt).toLocaleString() : '-'}</div>
                  </div>
                  {request.customerRequest.resolvedBy && (
                    <>
                      <div className="col-md-6">
                        <label className="text-muted small">Resolved By</label>
                        <div>{request.customerRequest.resolvedBy}</div>
                      </div>
                      <div className="col-md-6">
                        <label className="text-muted small">Resolved At</label>
                        <div>{request.customerRequest.resolvedAt ? new Date(request.customerRequest.resolvedAt).toLocaleString() : '-'}</div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="card mb-4">
              <div className="card-header bg-light">
                <h5 className="mb-0">
                  <i className="bi bi-info-circle me-2"></i>
                  Request Information
                </h5>
              </div>
              <div className="card-body">
                <div className="text-muted text-center py-3">
                  No customer request associated with this Slack event
                </div>
              </div>
            </div>
          )}

          {/* Section 3: Customer Info */}
          {request.customerRequest && (
            <div className="card mb-4">
              <div className="card-header bg-light">
                <h5 className="mb-0">
                  <i className="bi bi-person me-2"></i>
                  Customer Information
                </h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="text-muted small">Name</label>
                    <div className="fw-semibold">{request.customerRequest.customerName || '-'}</div>
                  </div>
                  <div className="col-md-4">
                    <label className="text-muted small">Phone</label>
                    <div>
                      {request.customerRequest.customerPhone ? (
                        <a href={`tel:${request.customerRequest.customerPhone}`}>{request.customerRequest.customerPhone}</a>
                      ) : (
                        '-'
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label className="text-muted small">Email</label>
                    <div>
                      {request.customerRequest.customerEmail ? (
                        <a href={`mailto:${request.customerRequest.customerEmail}`}>{request.customerRequest.customerEmail}</a>
                      ) : (
                        '-'
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Section 4: Notification History */}
          {request.customerRequest && (
            <div className="card mb-4">
              <div className="card-header bg-light">
                <h5 className="mb-0">
                  <i className="bi bi-bell me-2"></i>
                  Notification History
                </h5>
              </div>
              <div className="card-body">
                {request.customerRequest.notifications && (request.customerRequest.notifications.sms || request.customerRequest.notifications.email) ? (
                  <div className="row g-3">
                    {/* SMS Notification Card */}
                    <div className="col-md-6">
                      <div className={`card h-100 ${request.customerRequest.notifications.sms ? (request.customerRequest.notifications.sms.isSuccessful ? 'border-success' : 'border-danger') : 'border-secondary opacity-50'}`}>
                        <div className={`card-header d-flex align-items-center gap-2 ${request.customerRequest.notifications.sms ? (request.customerRequest.notifications.sms.isSuccessful ? 'bg-success bg-opacity-10' : 'bg-danger bg-opacity-10') : 'bg-light'}`}>
                          <i className="bi bi-chat-dots-fill text-info"></i>
                          <span className="fw-semibold">SMS</span>
                          {request.customerRequest.notifications.sms && (
                            <span className={`badge ms-auto ${request.customerRequest.notifications.sms.isSuccessful ? 'bg-success' : 'bg-danger'}`}>
                              <i className={`bi ${request.customerRequest.notifications.sms.isSuccessful ? 'bi-check-circle' : 'bi-x-circle'} me-1`}></i>
                              {request.customerRequest.notifications.sms.isSuccessful ? 'Sent' : 'Failed'}
                            </span>
                          )}
                        </div>
                        <div className="card-body">
                          {request.customerRequest.notifications.sms ? (
                            <>
                              <div className="mb-2">
                                <label className="text-muted small">Recipient</label>
                                <div className="font-monospace">{request.customerRequest.notifications.sms.recipient}</div>
                              </div>
                              <div className="mb-2">
                                <label className="text-muted small">Sent At</label>
                                <div className="small">{new Date(request.customerRequest.notifications.sms.sentAt).toLocaleString()}</div>
                              </div>
                              {!request.customerRequest.notifications.sms.isSuccessful && request.customerRequest.notifications.sms.errorMessage && (
                                <div className="alert alert-danger py-2 mb-0 small">
                                  <i className="bi bi-exclamation-triangle me-1"></i>
                                  {request.customerRequest.notifications.sms.errorMessage}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-muted text-center py-2">
                              <i className="bi bi-dash-circle me-1"></i>
                              No SMS notification sent
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Email Notification Card */}
                    <div className="col-md-6">
                      <div className={`card h-100 ${request.customerRequest.notifications.email ? (request.customerRequest.notifications.email.isSuccessful ? 'border-success' : 'border-danger') : 'border-secondary opacity-50'}`}>
                        <div className={`card-header d-flex align-items-center gap-2 ${request.customerRequest.notifications.email ? (request.customerRequest.notifications.email.isSuccessful ? 'bg-success bg-opacity-10' : 'bg-danger bg-opacity-10') : 'bg-light'}`}>
                          <i className="bi bi-envelope-fill text-secondary"></i>
                          <span className="fw-semibold">Email</span>
                          {request.customerRequest.notifications.email && (
                            <span className={`badge ms-auto ${request.customerRequest.notifications.email.isSuccessful ? 'bg-success' : 'bg-danger'}`}>
                              <i className={`bi ${request.customerRequest.notifications.email.isSuccessful ? 'bi-check-circle' : 'bi-x-circle'} me-1`}></i>
                              {request.customerRequest.notifications.email.isSuccessful ? 'Sent' : 'Failed'}
                            </span>
                          )}
                        </div>
                        <div className="card-body">
                          {request.customerRequest.notifications.email ? (
                            <>
                              <div className="mb-2">
                                <label className="text-muted small">Recipient</label>
                                <div className="font-monospace">{request.customerRequest.notifications.email.recipient}</div>
                              </div>
                              {request.customerRequest.notifications.email.subject && (
                                <div className="mb-2">
                                  <label className="text-muted small">Subject</label>
                                  <div className="small">{request.customerRequest.notifications.email.subject}</div>
                                </div>
                              )}
                              <div className="mb-2">
                                <label className="text-muted small">Sent At</label>
                                <div className="small">{new Date(request.customerRequest.notifications.email.sentAt).toLocaleString()}</div>
                              </div>
                              {!request.customerRequest.notifications.email.isSuccessful && request.customerRequest.notifications.email.errorMessage && (
                                <div className="alert alert-danger py-2 mb-0 small">
                                  <i className="bi bi-exclamation-triangle me-1"></i>
                                  {request.customerRequest.notifications.email.errorMessage}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-muted text-center py-2">
                              <i className="bi bi-dash-circle me-1"></i>
                              No email notification sent
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-muted text-center py-3">No notifications sent yet</div>
                )}
              </div>
            </div>
          )}

          {/* Section 5: Customer Responses */}
          {request.customerRequest && (
            <div className="card mb-4">
              <div className="card-header bg-light">
                <h5 className="mb-0">
                  <i className="bi bi-reply me-2"></i>
                  Customer Responses
                </h5>
              </div>
              <div className="card-body">
                {request.customerRequest.responses && request.customerRequest.responses.length > 0 ? (
                  <div className="d-flex flex-column gap-3">
                    {request.customerRequest.responses.map((response) => (
                      <div key={response.id} className="border rounded p-3 bg-light">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <span className="badge bg-secondary">
                            Tracking: {response.trackingNumber}
                          </span>
                          <small className="text-muted">
                            {new Date(response.respondedAt).toLocaleString()}
                          </small>
                        </div>
                        <pre
                          className="bg-white border rounded p-2 mb-0"
                          style={{
                            fontSize: '0.85rem',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            maxHeight: '200px',
                            overflow: 'auto',
                          }}
                        >
                          {formatJson(response.responseData)}
                        </pre>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-muted text-center py-3">No customer responses yet</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="col-lg-4">
          {/* Section 5: Thread Reply - PROMINENT */}
          <div className="card mb-4 border-primary">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">
                <i className="bi bi-chat-dots me-2"></i>
                Reply to Slack Thread
              </h5>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <textarea
                  className="form-control"
                  rows="5"
                  placeholder="Type your reply message here..."
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  disabled={sendingReply}
                  style={{ resize: 'vertical' }}
                ></textarea>
              </div>
              <button
                className="btn btn-primary w-100 d-flex align-items-center justify-content-center"
                onClick={handleSendReply}
                disabled={sendingReply || !replyMessage.trim()}
              >
                {sendingReply ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="bi bi-send me-2"></i>
                    Send Reply
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Section 6: Status Actions */}
          {request.customerRequest && (
            <div className="card mb-4">
              <div className="card-header bg-light">
                <h5 className="mb-0">
                  <i className="bi bi-gear me-2"></i>
                  Status Actions
                </h5>
              </div>
              <div className="card-body">
                <div className="d-flex flex-column gap-2">
                  <button
                    className={`btn ${request.customerRequest.status === 'Pending' ? 'btn-warning' : 'btn-outline-warning'}`}
                    onClick={() => handleStatusUpdate(STATUS_ENUM.Pending)}
                    disabled={updatingStatus || request.customerRequest.status === 'Pending'}
                  >
                    <i className="bi bi-clock me-2"></i>
                    Mark as Pending
                  </button>
                  <button
                    className={`btn ${request.customerRequest.status === 'Notified' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => handleStatusUpdate(STATUS_ENUM.Notified)}
                    disabled={updatingStatus || request.customerRequest.status === 'Notified'}
                  >
                    <i className="bi bi-bell me-2"></i>
                    Mark as Notified
                  </button>
                  <button
                    className={`btn ${request.customerRequest.status === 'CustomerResponded' ? 'btn-success' : 'btn-outline-success'}`}
                    onClick={() => handleStatusUpdate(STATUS_ENUM.CustomerResponded)}
                    disabled={updatingStatus || request.customerRequest.status === 'CustomerResponded'}
                  >
                    <i className="bi bi-chat-left-text me-2"></i>
                    Customer Responded
                  </button>
                  <button
                    className={`btn ${request.customerRequest.status === 'Resolved' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                    onClick={() => handleStatusUpdate(STATUS_ENUM.Resolved)}
                    disabled={updatingStatus || request.customerRequest.status === 'Resolved'}
                  >
                    <i className="bi bi-check-circle me-2"></i>
                    Resolve
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Agent Info */}
          {request.customerRequest && (request.customerRequest.agentSlackUserId || request.customerRequest.agentSlackUsername) && (
            <div className="card mb-4">
              <div className="card-header bg-light">
                <h5 className="mb-0">
                  <i className="bi bi-person-badge me-2"></i>
                  Assigned Agent
                </h5>
              </div>
              <div className="card-body">
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white"
                    style={{ width: 40, height: 40 }}
                  >
                    <i className="bi bi-person"></i>
                  </div>
                  <div>
                    <div className="fw-semibold">{request.customerRequest.agentSlackUsername || 'Unknown'}</div>
                    <small className="text-muted font-monospace">{request.customerRequest.agentSlackUserId}</small>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Resolve Modal */}
      {showResolveModal && (
        <>
          <div
            className="modal fade show"
            style={{ display: 'block' }}
            tabIndex="-1"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowResolveModal(false);
            }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-check-circle me-2"></i>
                    Resolve Request
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowResolveModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Your Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter your name"
                      value={resolvedBy}
                      onChange={(e) => setResolvedBy(e.target.value)}
                      autoFocus
                    />
                    <small className="text-muted">This will be recorded as the person who resolved this request.</small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowResolveModal(false)}
                    disabled={updatingStatus}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handleResolve}
                    disabled={updatingStatus || !resolvedBy.trim()}
                  >
                    {updatingStatus ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Resolving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        Resolve Request
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      <Footer />
    </div>
  );
}

export default CustomerRequestDetail;
